/**
 * Drawing surfaces that exist on the phone in your pocket.
 *
 * Everything here used `OffscreenCanvas` directly, which Safari only gained in
 * 16.4. On anything older the constructor throws, the surrounding try/catch
 * swallows it, and the caller gets `null` — so a photograph uploaded from an
 * iPhone got no rendition and no dimensions, and the grid showed nothing.
 * Silent, and indistinguishable from a decode failure.
 *
 * So: use it where it exists, fall back to a detached `<canvas>` where it does
 * not. The element is not attached to the document and is collected the moment
 * it goes out of scope, which is the same lifetime the offscreen one has.
 */

export type Surface =
  | { kind: "offscreen"; canvas: OffscreenCanvas }
  | { kind: "element"; canvas: HTMLCanvasElement };

const hasOffscreen =
  typeof OffscreenCanvas !== "undefined" &&
  // Existing is not enough — some builds ship the constructor without the
  // encoder, and `convertToBlob` is the half that actually makes a proxy.
  typeof OffscreenCanvas.prototype.convertToBlob === "function";

export function surface(width: number, height: number): Surface {
  if (hasOffscreen) {
    return { kind: "offscreen", canvas: new OffscreenCanvas(width, height) };
  }
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  return { kind: "element", canvas };
}

export function context2d(s: Surface): CanvasRenderingContext2D | null {
  const ctx = s.canvas.getContext("2d");
  return (ctx as CanvasRenderingContext2D | null) ?? null;
}

/**
 * Encode to a Blob.
 *
 * `convertToBlob` and `toBlob` take their options differently and one is a
 * promise while the other is a callback, which is exactly the sort of
 * difference that produces a fallback path nobody ever runs and which does not
 * work when they finally do.
 */
export async function toBlob(
  s: Surface,
  type = "image/jpeg",
  quality = 0.82,
): Promise<Blob | null> {
  if (s.kind === "offscreen") {
    try {
      return await s.canvas.convertToBlob({ type, quality });
    } catch {
      return null;
    }
  }
  return new Promise((resolve) => {
    try {
      s.canvas.toBlob((b) => resolve(b), type, quality);
    } catch {
      resolve(null);
    }
  });
}

/** Pixels back out, for anything that wants to look at them. */
export function imageData(
  s: Surface,
  width: number,
  height: number,
): ImageData | null {
  const ctx = context2d(s);
  if (!ctx) return null;
  try {
    return ctx.getImageData(0, 0, width, height);
  } catch {
    // Tainted canvas, or a surface too large for the device. Either way there
    // is nothing to read and pretending otherwise helps nobody.
    return null;
  }
}
